/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 19 2024
 **/

type PointType = { x: number; y: number };
type SizeType = { width: number; height: number };

export class Rect {
	private _point: PointType | null = null;
	private _size: SizeType | null = null;

	constructor(rect?: PointType & SizeType) {
		if (rect) {
			const { x, y, width, height } = rect;
			this._point = { x, y };
			this._size = { width, height };
		}
	}

	set point(value: PointType) {
		this._point = value;
	}

	set size(value: SizeType) {
		this._size = value;
	}

	get rect() {
		if (!this._point) throw Error("좌표를 설정해주세요.");
		if (!this._size) throw Error("크기를 설정해주세요.");
		return { ...this._point, ...this._size };
	}

	get point() {
		if (!this._point) throw Error("좌표를 설정해주세요.");
		return this._point;
	}

	get size() {
		if (!this._size) throw Error("크기를 설정해주세요.");
		return this._size;
	}

	public contains(rect: Rect["rect"]) {
		if (!this._point || !this._size) throw Error("좌표와 크기를 설정해주세요.");
		if (!rect.height || !rect.width || !rect.x || !rect.y) throw Error("비교할 대상을 정확하게 입력해주세요.");

		// 현재 Rect 객체의 좌표 및 크기
		const { x: myX, y: myY } = this._point;
		const { width: myWidth, height: myHeight } = this._size;

		// 비교할 Rect 객체의 좌표 및 크기
		const { x: rectX, y: rectY, width: rectWidth, height: rectHeight } = rect;

		// rect의 왼쪽 상단 모서리가 현재 Rect 내부에 있는지 확인
		const isTopLeftInside = rectX >= myX && rectX <= myX + myWidth && rectY >= myY && rectY <= myY + myHeight;

		// rect의 오른쪽 하단 모서리가 현재 Rect 내부에 있는지 확인
		const isBottomRightInside = rectX + rectWidth <= myX + myWidth && rectY + rectHeight <= myY + myHeight;

		return isTopLeftInside && isBottomRightInside;
	}

	public point_in_rectangle(pt: { x: number; y: number }) {
		if (!this._point || !this._size) throw Error("좌표와 크기를 설정해주세요.");
		const { x: myX, y: myY } = this._point;
		const { width: myWidth, height: myHeight } = this._size;

		return pt.x >= myX && pt.x <= myX + myWidth && pt.y >= myY && pt.y <= myY + myHeight;
	}

	public containsX(x: number) {
		if (!this._point || !this._size) throw Error("좌표와 크기를 설정해주세요.");

		// 현재 Rect 객체의 x 좌표와 너비
		const { x: myX } = this._point!;
		const { width: myWidth } = this._size!;

		// x 좌표가 현재 Rect의 x 경계 내에 있는지 확인
		return x >= myX && x <= myX + myWidth;
	}

	public containsY(y: number) {
		if (!this._point || !this._size) throw Error("좌표와 크기를 설정해주세요.");

		// 현재 Rect 객체의 y 좌표와 높이
		const { y: myY } = this._point!;
		const { height: myHeight } = this._size!;

		// y 좌표가 현재 Rect의 y 경계 내에 있는지 확인
		return y >= myY && y <= myY + myHeight;
	}
}

export class Point {
	private _point: PointType = { x: 0, y: 0 };

	constructor(x: number, y: number) {
		this._point = { x, y };
	}

	get x() {
		return this._point.x;
	}

	get y() {
		return this._point.y;
	}
}

export class Size {
	private _size: SizeType = { width: 0, height: 0 };

	constructor(width: number, height: number) {
		this._size = { width, height };
	}

	get width() {
		return this._size.width;
	}

	get height() {
		return this._size.height;
	}
}
